import type { SalarieDTO } from '@/features/api/extranet/dto/Demande/PROA'
import type { SalarieRechercheDTO } from '@/features/api/extranet/dto/Demande/shared/SalarieRechercheDTO'
import { useEventBus } from '@/features/application/app-events'
import { deepEqual } from '@/features/shared/tools/object'
import { storeToRefs } from 'pinia'
import { computed, type InjectionKey, watch } from 'vue'
import { buildDemandeActions, NOTIF_SAISIE_SALARIE_MODIFIÉE } from '../shared'
import { useDemandePROAStore } from './useDemandePROAStore'

export type DemandeProAActions = ReturnType<typeof useDemandePROAActions>
export const PROA_ACTIONS: InjectionKey<DemandeProAActions> = Symbol('demande-inject')

export function useDemandePROAActions() {
  const store = useDemandePROAStore()
  const { model } = storeToRefs(store)
  const eventBus = useEventBus()

  const inscriptionPoleEmploiDisabled = computed(() => {
    return model.value?.salarie?.inscritPoleEmploi === false
  })

  // typeDocumentTemplate: computed(() => 'Cerfa'),
  const actionsDemande = buildDemandeActions(store)

  // Utilisé par la recherche du salarié
  function créerSalariéVide(): SalarieDTO {
    return {
      source: 'Portail',
      adresse: {
        codePostal: null,
        commune: null,
        complement: null,
        mentionsSpeciales: null,
        voie: null,
      },
      beneficieReconnaissanceTravailleurHandicape: null,
      communeNaissance: null,
      csp: null,
      dateNaissance: null,
      departementNaissance: null,
      diplomeOuTitreLePlusEleveObtenu: null,
      dureeMoisPoleEmploi: null,
      email: null,
      inscritPoleEmploi: null,
      nir: null,
      nirTemporaire: null,
      nom: null,
      nomNaissance: null,
      numeroInscriptionPoleEmploi: null,
      prenom: null,
      sexe: null,
      signature: null,
      situation: null,
      telephone: null,
      typeMinimuSocial: null,
    }
  }

  const patchRemplacerSalarié = store.setupCustomUpdater<SalarieRechercheDTO>((patch, salarieRecherché) => {
    const { natureContrat, niveauFormation, siren, nic, ...salarieRecherchéClean } = salarieRecherché
    const salarie = {
      ...créerSalariéVide(),
      ...salarieRecherchéClean,
    }
    patch.salarie = salarie
  })

  // PRT-589 - modifie origine Nir
  const patchSourceSalariéToPortail = store.setupCustomUpdater<{ resetNir: boolean }>((patch, payload) => {
    if (!patch.salarie) return
    patch.salarie.source = 'Portail'
    patch.salarie.signature = null
    if (payload.resetNir) {
      patch.salarie.nir = null
      /* Notifie utilisateur */
      eventBus?.dispatch(NOTIF_SAISIE_SALARIE_MODIFIÉE)
    }
  })

  watch(
    () => ({
      nir: model.value?.salarie?.nir,
      signature: model.value?.salarie?.signature,
    }),
    (newValue, oldValue) => {
      if (model.value?.salarie?.source === 'Portail') return
      if (oldValue.signature !== newValue.signature) return
      if (deepEqual(newValue, oldValue)) return
      patchSourceSalariéToPortail({
        resetNir: false,
      })
    },
  )

  watch(
    () => ({
      nomNaissance: model.value?.salarie?.nomNaissance,
      prenom: model.value?.salarie?.prenom,
      sexe: model.value?.salarie?.sexe,
      dateNaissance: model.value?.salarie?.dateNaissance,
      nirTemporaire: model.value?.salarie?.nirTemporaire,
      signature: model.value?.salarie?.signature,
    }),
    (newValue, oldValue) => {
      if (model.value?.salarie?.source === 'Portail') return
      if (oldValue.signature !== newValue.signature) return
      if (deepEqual(newValue, oldValue)) return
      patchSourceSalariéToPortail({
        resetNir: true,
      })
    },
  )

  return {
    ...actionsDemande,
    inscriptionPoleEmploiDisabled,
    patchRemplacerSalarié,
  }
}
